import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format, differenceInDays } from "date-fns";
import { fr, enUS, es } from "date-fns/locale";
import { Eye, EyeOff, Trash2 } from "lucide-react";

interface TraineeSupervisionRequestsProps {
  requests: any[];
  onUpdate: () => void;
}

export const TraineeSupervisionRequests = ({ requests, onUpdate }: TraineeSupervisionRequestsProps) => {
  const [loading, setLoading] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [showRevokedRequests, setShowRevokedRequests] = useState(false);
  const { toast } = useToast();
  const { t, i18n } = useTranslation('supervision');

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'es': return es;
      case 'en': return enUS;
      default: return fr;
    }
  };

  const handleRequestResponse = async (requestId: string, status: 'accepted' | 'declined') => {
    try {
      setLoading(requestId);
      
      // Récupérer d'abord les détails de la demande
      const { data: requestData, error: fetchError } = await supabase
        .from("supervision_requests")
        .select("*")
        .eq("id", requestId)
        .single();

      if (fetchError) throw fetchError;

      // Mettre à jour le statut de la demande
      const { error } = await supabase
        .from("supervision_requests")
        .update({ 
          status, 
          responded_at: new Date().toISOString() 
        })
        .eq("id", requestId);

      if (error) throw error;

      // Les permissions sont créées automatiquement par le trigger handle_supervision_request_accepted
      if (status === 'accepted') {
        toast({
          title: t('common.success'),
          description: t('management.success.requestAccepted'),
        });
      } else {
        toast({
          title: t('common.success'),
          description: t('management.success.requestDeclined'),
        });
      }

      onUpdate();
    } catch (error: any) {
      console.error("Erreur lors de la réponse à la demande:", error);
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: error.message,
      });
    } finally {
      setLoading(null);
    }
  };

  const handleDeleteRequest = async (requestId: string) => {
    try {
      setDeleting(requestId);
      
      const { error } = await supabase
        .from("supervision_requests")
        .delete()
        .eq("id", requestId);

      if (error) throw error;

      toast({
        title: t('common.success'),
        description: t('management.success.requestDeleted'),
      });

      onUpdate();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: error.message,
      });
    } finally {
      setDeleting(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: "default" | "secondary" | "destructive" | "outline" } = {
      pending: "outline",
      accepted: "default",
      declined: "destructive",
      revoked: "secondary"
    };

    return (
      <Badge variant={variants[status] || "outline"}>
        {t(`management.trainee.status.${status}`)}
      </Badge>
    );
  };

  // Filtrer les demandes révoquées/refusées de plus de 2 semaines
  const shouldHideOldRequest = (request: any) => {
    if (request.status !== 'revoked' && request.status !== 'declined') return false;
    if (!request.responded_at) return false;
    
    const respondedDate = new Date(request.responded_at);
    const daysSinceResponded = differenceInDays(new Date(), respondedDate);
    return daysSinceResponded > 14;
  };

  const filteredRequests = showRevokedRequests 
    ? requests 
    : requests.filter(request => !shouldHideOldRequest(request));

  const hiddenCount = requests.length - filteredRequests.length;

  if (requests.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-gray-500">
          {t('management.trainee.requests.noRequests')}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {hiddenCount > 0 && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowRevokedRequests(!showRevokedRequests)}
          className="mb-2"
        >
          {showRevokedRequests ? (
            <>
              <EyeOff className="h-4 w-4 mr-2" />
              {t('management.coach.revokedRequests.hide')}
            </>
          ) : (
            <>
              <Eye className="h-4 w-4 mr-2" />
              {t('management.coach.revokedRequests.hidden', { count: hiddenCount })}
            </>
          )}
        </Button>
      )}

      {filteredRequests.map((request) => (
        <Card key={request.id}>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-lg">
                  {request.coaches?.coach_name || t('coach.unknownCoach')}
                </CardTitle>
                <p className="text-sm text-gray-600">
                  {request.coaches?.contact_email}
                </p>
                <p className="text-sm text-gray-500">
                  {format(new Date(request.requested_at), "PPP", { locale: getDateLocale() })}
                </p>
              </div>
              {getStatusBadge(request.status)}
            </div>
          </CardHeader>
          <CardContent>
            {request.message && (
              <p className="text-sm mb-4 p-3 bg-gray-50 rounded">
                {request.message}
              </p>
            )}
            
            {request.status === 'pending' && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => handleRequestResponse(request.id, 'accepted')}
                  disabled={loading === request.id}
                >
                  {t('management.trainee.actions.accept')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleRequestResponse(request.id, 'declined')}
                  disabled={loading === request.id}
                >
                  {t('management.trainee.actions.decline')}
                </Button>
              </div>
            )}

            {(request.status === 'revoked' || request.status === 'declined') && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleDeleteRequest(request.id)}
                disabled={deleting === request.id}
                className="flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                {deleting === request.id ? t('management.actions.deleting') : t('management.actions.delete')}
              </Button>
            )}
          </CardContent>
        </Card>
      ))}

      {filteredRequests.length === 0 && hiddenCount > 0 && (
        <Card>
          <CardContent className="p-6 text-center text-gray-500">
            {t('management.trainee.requests.allProcessed')}
          </CardContent>
        </Card>
      )}
    </div>
  );
};